import React, { useEffect, useState } from 'react';
import { useDebounce } from 'react-use';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { SimpleSingleDropdown } from '@grnhse/seedling/lib/birch/components/forms/dropdown';
import { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import TextInput from '@grnhse/seedling/lib/birch/components/forms/text_input';
import type AdvancedFilter from 'talent_matching/components/filters/advanced_filter';
import { useUserContext } from 'talent_matching/contexts/user_context';
import styled from 'styled-components';
import { withLocalePrefix } from 'shared/utils/translation';

const t = withLocalePrefix('talent_matching.filter_row.advanced_filters');

const CurrencyContainer = styled.div`
  flex: 0 0 120px;
  min-width: 120px;
`;

const CurrencyValue = styled.div`
  flex: 1 1 0;
  min-width: 0;
`;

type Props = {
  filter: AdvancedFilter;
  onValueChange: (newValue: string | Item | Item[] | null) => void;
};

const CurrencyFilter = ({ filter, onValueChange }: Props) => {
  const {
    advancedFilterConfig: { currencies },
  } = useUserContext();

  const filterValue = (filter.value || '') as string;
  const currencyType = filterValue.split('$')[0] || 'USD';
  const selectedCurrency =
    currencies.find(
      (c) => c.value.toLowerCase() === currencyType.toLowerCase()
    ) || currencies[0];

  const [minValue, maxValue] = (filterValue.split('$')[1] || ':').split(':');
  const initialValue = minValue || maxValue;
  const [currencyValue, setCurrencyValue] = useState(initialValue);

  const updateFilterValue = (newCurrency: string, newValue: string) => {
    switch (filter.operator?.value) {
      case 'equal_to':
        onValueChange(`${newCurrency}$${newValue}:${newValue}`);
        break;
      case 'greater_than':
        onValueChange(`${newCurrency}$${newValue}:`);
        break;
      case 'less_than':
        onValueChange(`${newCurrency}$:${newValue}`);
        break;
    }
  };

  const onCurrencyChange = (item: Item) => {
    const newCurrencyType = item.value;
    updateFilterValue(newCurrencyType, currencyValue);
  };

  useDebounce(
    () => {
      updateFilterValue(selectedCurrency.value, currencyValue);
    },
    500,
    [currencyValue]
  );

  useEffect(() => {
    updateFilterValue(selectedCurrency.value, currencyValue);
  }, [filter.operator?.value]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Stack direction="row" flexWrap="nowrap" spacing={1} sx={{ width: '100%' }}>
      <CurrencyContainer>
        <SimpleSingleDropdown
          ariaLabel={t('value')}
          selectedItem={selectedCurrency}
          onSelectedItemChange={onCurrencyChange}
          items={currencies}
          clearable={false}
        />
      </CurrencyContainer>
      <CurrencyValue>
        <TextInput
          ariaLabel={t('value')}
          placeholder={t('value')}
          value={currencyValue}
          onChange={(e) => setCurrencyValue(e.target.value)}
          type="number"
          min={0}
        />
      </CurrencyValue>
    </Stack>
  );
};

export default CurrencyFilter;
